import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Api } from "../lib/api.js";
import { IS_TV } from "../lib/tvMode.js";
import { getActiveProfileId } from "../lib/profile.js";

/**
 * TV Lobby — the permanent screensaver shown on the living-room TV.
 *
 * Three modes:
 *   AP mode   — no network; Pi started a hotspot. Shows QR for WiFi provisioning.
 *   Offline   — Pi can't reach the server. Shows offline message.
 *   Cast mode — normal operation. Shows After Dark screensaver; waits for casts.
 */

const TV_LABEL = "Living Room TV";
const WIFI_SETUP_URL = "http://192.168.4.1/wifi";

function getDeviceLabel(): string {
  // ?label= pins the receiver's display name (same bootstrap pattern as
  // device_id) — without it two kiosks both register as "Living Room TV" and
  // the cast picker can't tell them apart (two-Pi household, 2026-10-21).
  const key = "wallace_tv_device_label";
  const pinned = new URLSearchParams(window.location.search).get("label");
  if (pinned) {
    localStorage.setItem(key, pinned);
    return pinned;
  }
  return localStorage.getItem(key) || TV_LABEL;
}

function getDeviceId(): string {
  const key = "wallace_tv_device_id";
  // A device_id URL param pins the kiosk's identity (same bootstrap pattern as
  // ?token=): survives a wiped localStorage, which otherwise mints a new
  // receiver on every fresh profile and strands queued casts on the old id.
  const pinned = new URLSearchParams(window.location.search).get("device_id");
  if (pinned) {
    localStorage.setItem(key, pinned);
    return pinned;
  }
  let id = localStorage.getItem(key);
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => { const r = Math.random() * 16 | 0; return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16); });
    localStorage.setItem(key, id);
  }
  return id;
}

function watchPathFor(cmd: { item_id: string; kind: string; queued_at?: number }): string {
  // &cast= makes every queued command a distinct nav target — the Watch page
  // keys its whole state on it, so re-casting the same item re-launches mpv
  // instead of landing on a page that thinks mpv was already started.
  const castTag = `&cast=${cmd.queued_at ?? Date.now()}`;
  // YouTube items must land on the dedicated YouTube player (mpv+yt-dlp with
  // self-recovery back to the lobby), never the generic watch page. Library
  // ids are "youtube:<source>:<videoId>"; legacy form is "yt:<videoId>".
  if (cmd.kind === "youtube" || cmd.kind === "youtube_video" || cmd.item_id.startsWith("yt:") || cmd.item_id.startsWith("youtube:")) {
    const parts = cmd.item_id.split(":");
    const videoId = cmd.item_id.startsWith("yt:") ? parts[1]! : parts[parts.length - 1]!;
    return `/watch/yt/${encodeURIComponent(videoId)}?autoplay=1${castTag}`;
  }
  return `/watch/${encodeURIComponent(cmd.item_id)}?autoplay=1${castTag}`;
}

export function TVLobby(): JSX.Element {
  const nav = useNavigate();
  const deviceId = useRef(getDeviceId());
  const deviceLabel = useRef(getDeviceLabel());
  const [lastCast, setLastCast] = useState<string | null>(null);
  const [apMode, setApMode] = useState<boolean | null>(null); // null = checking
  const [offline, setOffline] = useState(false);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [screenFrozen, setScreenFrozen] = useState(false);

  // Poll network status every 10 s; switch screens as it changes.
  useEffect(() => {
    let cancelled = false;
    const check = async (): Promise<void> => {
      try {
        const data = await fetch("/wifi/status").then((r) => r.json() as Promise<{
          ap_mode: boolean;
          installed_version?: string | null;
        }>);
        if (!cancelled) {
          setApMode(data.ap_mode);
          setOffline(false);
          if (data.installed_version) setInstalledVersion(data.installed_version);
        }
      } catch {
        if (!cancelled) {
          setApMode(false);
          setOffline(true);
        }
      }
    };
    void check();
    const t = window.setInterval(() => void check(), 10_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);

  // Cast heartbeat — only runs when we have a real network.
  useEffect(() => {
    if (apMode !== false) return;
    const send = (): void => {
      void Api.cast.register(deviceId.current, deviceLabel.current).catch(() => undefined);
    };
    send();
    const t = window.setInterval(send, 30_000);
    return () => window.clearInterval(t);
  }, [apMode]);

  // Cast command poll — only runs when we have a real network.
  useEffect(() => {
    if (apMode !== false) return;
    const t = window.setInterval(async () => {
      try {
        const { command } = await Api.cast.pending(deviceId.current);
        if (command) {
          setLastCast(command.title ?? command.item_id);
          setScreenFrozen(true); // stop screensaver immediately — free CPU for mpv
          nav(watchPathFor(command));
        }
      } catch { /* network blip */ }
    }, 2500);
    return () => window.clearInterval(t);
  }, [apMode, nav]);

  // Boot resume — a Pi crash/reboot strands the evening on this lobby with the
  // episode still mid-way (2026-09-20). Once per page session: if nothing is
  // playing and no cast is queued, pick the most recent in-progress item
  // (watched 10 min–4 h ago, ≥60 s in, not finished) and put it back on.
  // The 10-min floor is what stops a bail-to-lobby from re-firing the same
  // item forever; the sessionStorage flag makes it once per boot.
  useEffect(() => {
    if (!IS_TV || apMode !== false) return;
    if (sessionStorage.getItem("wt_boot_resume_done")) return;
    const t = window.setTimeout(async () => {
      sessionStorage.setItem("wt_boot_resume_done", "1");
      try {
        const st = await fetch("/api/cast/mpv/status").then((r) => r.json()) as { active?: boolean };
        if (st.active) return;
        const { command } = await Api.cast.pending(deviceId.current);
        if (command) return; // the cast poll is on it — don't race a human
        const profileId = getActiveProfileId();
        if (!profileId) return;
        const hist = await Api.history(profileId);
        const now = Date.now();
        const cand = (hist.items ?? []).find((i) => {
          const seenMs = i.last_seen_at ? Date.parse(i.last_seen_at) : 0;
          const pos = i.last_position_s ?? 0;
          const dur = i.duration_s ?? null;
          if (!seenMs || now - seenMs < 10 * 60_000 || now - seenMs > 4 * 3_600_000) return false;
          if (pos < 60) return false;
          if (dur && dur > 0 && pos / dur >= 0.92) return false;
          return true;
        });
        if (cand) {
          setLastCast(`Resuming ${cand.title ?? cand.id}`);
          nav(watchPathFor({ item_id: cand.id, kind: cand.kind }));
        }
      } catch { /* offline, empty history — stay on the lobby */ }
    }, 8_000); // let the server settle after boot before planning a transcode
    return () => window.clearTimeout(t);
  }, [apMode, nav]);

  // One-shot error notice — the Watch page leaves a reason here when it bails
  // (e.g. a source that stalls twice), so the lobby doesn't look silently
  // broken. Read once, then cleared.
  const [bootError, setBootError] = useState<string | null>(() => {
    try {
      const v = sessionStorage.getItem("wt_watch_error");
      if (v) sessionStorage.removeItem("wt_watch_error");
      return v;
    } catch { return null; }
  });

  if (apMode === null) {
    return <div className="min-h-screen bg-lacquer" />;
  }

  if (offline) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-lacquer-deep select-none"
        style={{ fontFamily: "'Cinzel', serif" }}>
        <div className="flex flex-col items-center gap-6 text-center px-8 max-w-2xl">
          <div className="section-label text-parchment/40 tracking-[0.4em] text-sm">NO CONNECTION</div>
          <h1 className="font-display text-ivory/60 text-4xl" style={{ letterSpacing: "0.06em" }}>Offline</h1>
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className="block w-1.5 h-1.5 rounded-full bg-parchment/20 animate-pulse"
                style={{ animationDelay: `${i * 300}ms` }} />
            ))}
          </div>
          {/* How to get back online — connect via the WallaceTV hotspot */}
          <div className="mt-4 text-left text-parchment/80 leading-relaxed border border-hairline rounded-lg p-6 bg-lacquer/40">
            <div className="section-label text-gold tracking-[0.3em] text-xs mb-3">CONNECT TO WI‑FI</div>
            <ol className="space-y-2 text-base list-decimal list-inside">
              <li>On your phone, join the Wi‑Fi network <span className="text-ivory font-semibold">WallaceTV</span> (open network, no password)</li>
              <li>Open <span className="text-ivory font-mono">http://192.168.4.1/wifi</span> in your browser</li>
              <li>Pick your network, enter its password, and tap <span className="text-ivory">Connect</span></li>
            </ol>
            <div className="text-parchment/50 text-sm mt-3">The TV reconnects automatically once it joins.</div>
          </div>
        </div>
        <VersionBadge version={installedVersion} />
      </div>
    );
  }

  if (apMode) {
    return <WifiSetupScreen version={installedVersion} />;
  }

  return <Screensaver lastCast={bootError ?? lastCast} version={installedVersion} frozen={screenFrozen} />;
}

// ---------------------------------------------------------------------------
// Shared version badge — fixed bottom-right corner, shown on all TV screens.
// ---------------------------------------------------------------------------
function VersionBadge({ version }: { version: string | null }): JSX.Element {
  if (!version) return <></>;
  return (
    <div className="fixed bottom-3 right-4 font-mono text-[10px] text-parchment/30 tracking-widest select-none pointer-events-none">
      {version}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WiFi setup screen — shown on the TV when the Pi is in hotspot mode.
// ---------------------------------------------------------------------------
function WifiSetupScreen({ version }: { version: string | null }): JSX.Element {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-lacquer-deep select-none"
      style={{ fontFamily: "'Cinzel', serif" }}
    >
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(10,4,4,0.75) 100%)",
        }}
      />

      <div className="relative z-10 flex flex-col items-center gap-8 px-8 text-center">
        <div>
          <div className="section-label text-vermilion tracking-[0.4em] text-sm mb-2">
            NO NETWORK
          </div>
          <h1
            className="font-display text-ivory text-4xl leading-tight"
            style={{ letterSpacing: "0.06em" }}
          >
            WiFi Setup
          </h1>
          <div className="hero-underline mx-auto mt-4 w-48" />
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="bg-ivory p-5 rounded-2xl shadow-xl">
            <QRCodeSVG value={WIFI_SETUP_URL} size={200} level="M" />
          </div>
          <p className="text-parchment/80 text-base max-w-[34ch] leading-relaxed">
            Connect your phone to the <strong className="text-gold">WallaceTV</strong> Wi‑Fi
            hotspot, then scan this code to set up the network.
          </p>
          <div className="font-mono text-gold/80 text-sm tracking-widest">
            {WIFI_SETUP_URL}
          </div>
        </div>

        <div className="flex gap-1.5 mt-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block w-1.5 h-1.5 rounded-full bg-vermilion/60 animate-pulse"
              style={{ animationDelay: `${i * 300}ms` }}
            />
          ))}
        </div>
      </div>
      <VersionBadge version={version} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flying Toasters screensaver — uses the original After Dark sprites from
// github.com/robertventurini/FlyingToasters (via bryanbraun/after-dark-css).
//
// toaster-sprite.gif: 256×64px, 4 frames of 64×64px arranged horizontally.
// toast0-3.gif: 64×64px each (static per doneness level).
// Flight: diagonal top-right → bottom-left, matching original After Dark.
// Wing flap: CSS background-position steps(4) at 0.2s per cycle.
// ---------------------------------------------------------------------------

const DONENESS_IDX: Record<string, number> = { light: 0, golden: 1, dark: 2, burnt: 3 };

// Seconds to cross the screen per speed setting
const SPEED_DURATION: Record<string, number> = {
  snail: 28, slow: 18, medium: 12, fast: 6, lightning: 3,
};

// Sprite rendered at 2× native (128×128px) for visibility on a TV
const SPRITE_PX = 128;
const SPRITE_SHEET_W = SPRITE_PX * 4; // 512px for the 4-frame sheet

type Flyer = {
  id: number;
  kind: 'toaster' | 'toast';
  startTop: string;
  startLeft: string;
  duration: number;
  flapReverse: boolean; // alternate wing direction per sprite for variety
};
let _flyerId = 0;

function mkFlyer(baseDuration: number, seq: number): Flyer {
  const isFast = seq % 4 === 3;
  const duration = (isFast ? baseDuration * 0.7 : baseDuration) * (0.8 + Math.random() * 0.4);

  // Start off the top edge or off the right edge (top-right quadrant)
  const fromTop = Math.random() < 0.5;
  const startTop  = fromTop
    ? `${-18 - Math.random() * 15}%`
    : `${-5 + Math.random() * 55}%`;
  const startLeft = fromTop
    ? `${35 + Math.random() * 75}%`
    : `${100 + Math.random() * 15}%`;

  return {
    id: _flyerId++,
    kind: seq % 2 === 1 ? 'toast' : 'toaster',
    startTop,
    startLeft,
    duration,
    flapReverse: seq % 3 === 0,
  };
}

function Screensaver({ lastCast, version, frozen }: { lastCast: string | null; version: string | null; frozen?: boolean }): JSX.Element {
  const [settings, setSettings] = useState({ speed: "medium", doneness: "golden", density: 6 });
  const [flyers, setFlyers] = useState<Flyer[]>([]);
  const seqRef = useRef(0);

  // Load settings from server
  useEffect(() => {
    fetch("/api/screensaver/settings")
      .then(r => r.json() as Promise<{ speed: string; doneness: string; density: number }>)
      .then(s => setSettings(s))
      .catch(() => undefined);
  }, []);

  // Spawn flyers — seed an initial batch then drip in new ones.
  // Frozen immediately when a cast command arrives to free CPU for mpv.
  useEffect(() => {
    if (frozen) { setFlyers([]); return; }
    const baseDuration = SPEED_DURATION[settings.speed] ?? 12;
    const interval = Math.max(350, 9000 / settings.density);

    setFlyers(Array.from({ length: Math.min(settings.density, 6) }, () =>
      mkFlyer(baseDuration, seqRef.current++)
    ));

    const t = window.setInterval(() => {
      setFlyers(prev => {
        const next = [...prev, mkFlyer(baseDuration, seqRef.current++)];
        const max = settings.density * 4;
        return next.length > max ? next.slice(-max) : next;
      });
    }, interval);
    return () => window.clearInterval(t);
  }, [settings.speed, settings.density, frozen]);

  const toastIdx = DONENESS_IDX[settings.doneness] ?? 1;

  return (
    <div className="min-h-screen bg-black overflow-hidden select-none relative">
      {flyers.map(f => (
        // Outer div: handles diagonal flight
        <div
          key={f.id}
          className="absolute pointer-events-none"
          style={{
            top: f.startTop,
            left: f.startLeft,
            width: SPRITE_PX,
            height: SPRITE_PX,
            animation: `ad-fly ${f.duration}s linear forwards`,
            willChange: 'transform',
          }}
        >
          {f.kind === 'toaster' ? (
            // Toaster: sprite sheet cycled via background-position steps(4)
            <div style={{
              width: SPRITE_PX,
              height: SPRITE_PX,
              backgroundImage: 'url(/saver/toaster-sprite.gif)',
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${SPRITE_SHEET_W}px ${SPRITE_PX}px`,
              backgroundPosition: '0 0',
              animation: `ad-flap${f.flapReverse ? '-r' : ''} 0.2s steps(4) infinite`,
            }} />
          ) : (
            // Toast: static GIF per doneness level
            <img
              src={`/saver/toast${toastIdx}.gif`}
              width={SPRITE_PX}
              height={SPRITE_PX}
              alt=""
              style={{ display: 'block' }}
            />
          )}
        </div>
      ))}

      {lastCast && (
        <div className="absolute bottom-10 left-0 right-0 text-center font-mono text-white/20 text-xs tracking-widest uppercase">
          Last: {lastCast}
        </div>
      )}
      <VersionBadge version={version} />
      <style>{`
        /* Diagonal flight: top-right → bottom-left, matching original After Dark */
        @keyframes ad-fly {
          from { transform: translate(0, 0); }
          to   { transform: translate(-150vw, 150vh); }
        }
        /* Wing flap — forward and reverse variants for variety */
        @keyframes ad-flap {
          from { background-position-x: 0px; }
          to   { background-position-x: -${SPRITE_SHEET_W}px; }
        }
        @keyframes ad-flap-r {
          from { background-position-x: -${SPRITE_SHEET_W}px; }
          to   { background-position-x: 0px; }
        }
      `}</style>
    </div>
  );
}
